import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { requestAdminMagicLink } from "@/lib/admin-auth.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in | Admin | Scott Cunningham" },
      { name: "description", content: "Sign in to manage Scott Cunningham’s website." },
      { property: "og:title", content: "Sign in | Admin | Scott Cunningham" },
      { property: "og:description", content: "Sign in to manage Scott Cunningham’s website." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Sign in | Admin | Scott Cunningham" },
      { name: "twitter:description", content: "Sign in to manage Scott Cunningham’s website." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState<Date | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/admin" });
    });
  }, [navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/admin`;
      const res = await requestAdminMagicLink({ data: { email, password, redirectTo } });
      setSent(true);
      setLastSentAt(new Date());
      setCooldown(res?.cooldown ?? 60);
      toast.success("Sign-in link sent. Check your admin inbox.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign in failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      const redirectTo = `${window.location.origin}/admin`;
      const res = await requestAdminMagicLink({ data: { email, password, redirectTo } });
      setLastSentAt(new Date());
      setCooldown(res?.cooldown ?? 60);
      toast.success("A new sign-in link has been sent.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not resend";
      setError(msg);
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
        <h1 className="font-display text-4xl">Check your email</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Password verified. A one-time sign-in link has been sent to your admin inbox
          {lastSentAt ? ` at ${lastSentAt.toLocaleTimeString()}` : ""}. Click the link to finish
          signing in.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Links are single-use and expire after about an hour. Sending a new one invalidates earlier
          links.
        </p>
        {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
        <div className="mt-6">
          <Button onClick={resend} disabled={cooldown > 0 || resending} className="w-full">
            {resending
              ? "Sending…"
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : "Resend sign-in link"}
          </Button>
        </div>
        <Link to="/" className="mt-6 text-xs text-muted-foreground">
          ← Back home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
      <h1 className="font-display text-4xl">Sign in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Admin access only. After your password is verified, a one-time sign-in link is emailed to
        the admin inbox.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? "…" : "Send sign-in link"}
        </Button>
      </form>
      <Link to="/" className="mt-6 text-xs text-muted-foreground">
        ← Back home
      </Link>
    </div>
  );
}
